import { useState, useEffect } from "react";
import { supabase } from "../lib/supabaseClient";
import { useToast } from "../ToastContext";
import Papa from "papaparse";

export default function CouponManager({ user }) {
  const { addToast } = useToast();
  const [qrs, setQrs] = useState([]);
  const [selectedQR, setSelectedQR] = useState("");
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    supabase.from("qr_codes").select("id, name, qr_mode")
      .eq("user_id", user.id)
      .eq("qr_mode", "direct")
      .then(({ data }) => setQrs(data || []));
  }, [user?.id]);

  useEffect(() => {
    if (!selectedQR) return;
    fetchCoupons();
  }, [selectedQR]);

  const fetchCoupons = async () => {
    setLoading(true);
    const { data } = await supabase.from("coupons")
      .select("*").eq("qr_id", selectedQR)
      .order("created_at", { ascending: false });
    setCoupons(data || []);
    setLoading(false);
  };

  const handleUploadCSV = async () => {
    if (!csvText.trim() || !selectedQR) return;
    setUploading(true);
    try {
      const codes = csvText.split("\n").map(c => c.trim()).filter(Boolean);
      const rows = codes.map(code => ({
        qr_id: selectedQR,
        user_id: user.id,
        code,
        used: false
      }));
      const { error } = await supabase.from("coupons").insert(rows);
      if (error) throw error;
      addToast(`${codes.length} coupons uploaded!`, "success");
      setCsvText("");
      fetchCoupons();
    } catch (err) {
      addToast(err.message, "error");
    } finally {
      setUploading(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    Papa.parse(file, {
      complete: (results) => {
        const codes = results.data.flat().map(c => String(c).trim()).filter(Boolean);
        setCsvText(codes.join("\n"));
      }
    });
  };

  const exportCSV = () => {
    const rows = coupons.map(c => `${c.code},${c.used ? "Used" : "Available"},${c.used_at || ""}`);
    const csv = ["Code,Status,Used At", ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `coupons_${selectedQR}.csv`;
    a.click();
  };

  const stats = {
    total: coupons.length,
    used: coupons.filter(c => c.used).length,
    available: coupons.filter(c => !c.used).length,
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-black text-white uppercase italic tracking-tighter">Coupon Manager</h1>
        <p className="text-gray-500 text-[10px] uppercase tracking-widest mt-1">Each scan shows a unique coupon code</p>
      </div>

      {/* QR selector */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-4">
        <p className="text-[10px] font-black uppercase text-gray-500 tracking-widest">Select QR Code</p>
        <select
          value={selectedQR}
          onChange={e => setSelectedQR(e.target.value)}
          className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-white outline-none"
        >
          <option value="">Choose a QR code...</option>
          {qrs.map(qr => <option key={qr.id} value={qr.id}>{qr.name || qr.id}</option>)}
        </select>
        {selectedQR && (
          <p className="text-[9px] text-gray-600">Enable coupon mode in the QR's Advanced settings to activate this feature.</p>
        )}
      </div>

      {selectedQR && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: "Total", value: stats.total, color: "text-white" },
              { label: "Available", value: stats.available, color: "text-green-400" },
              { label: "Used", value: stats.used, color: "text-red-400" },
            ].map(s => (
              <div key={s.label} className="bg-white/5 border border-white/10 rounded-2xl p-4 text-center">
                <p className="text-[9px] font-black uppercase text-gray-500 tracking-widest">{s.label}</p>
                <p className={`text-3xl font-black mt-1 ${s.color}`}>{s.value}</p>
              </div>
            ))}
          </div>

          {/* Upload */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-4">
            <p className="text-[10px] font-black uppercase text-gray-500 tracking-widest">Upload Coupon Codes</p>
            <div className="border-2 border-dashed border-white/10 rounded-xl p-6 text-center relative hover:border-blue-500/50 transition-all">
              <input type="file" accept=".csv,.txt" onChange={handleFileUpload} className="absolute inset-0 opacity-0 cursor-pointer" />
              <p className="text-gray-500 text-sm">Drop CSV file here or click to upload</p>
              <p className="text-[9px] text-gray-600 mt-1">One coupon code per row</p>
            </div>
            <p className="text-[10px] text-gray-500 font-black uppercase">Or paste codes manually:</p>
            <textarea
              value={csvText}
              onChange={e => setCsvText(e.target.value)}
              placeholder={"SAVE10\nDISCOUNT20\nFREEGIFT"}
              rows={5}
              className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-white text-sm font-mono outline-none focus:border-blue-500"
            />
            <button
              onClick={handleUploadCSV}
              disabled={uploading || !csvText.trim()}
              className="w-full py-3 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 rounded-xl text-[10px] font-black uppercase text-white transition-all"
            >
              {uploading ? "Uploading..." : "Upload Codes"}
            </button>
          </div>

          {/* Coupon list */}
          <div className="bg-white/5 border border-white/10 rounded-[32px] overflow-hidden">
            <div className="p-6 border-b border-white/10 flex justify-between items-center">
              <h3 className="text-sm font-black uppercase tracking-widest">Coupon Codes</h3>
              <button onClick={exportCSV} className="px-4 py-2 bg-white/5 rounded-xl text-[10px] font-black uppercase text-gray-400 hover:bg-white/10 transition-all">
                Export CSV
              </button>
            </div>
            {loading ? (
              <div className="p-10 flex justify-center">
                <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : coupons.length === 0 ? (
              <div className="p-10 text-center text-gray-500 text-sm">No coupons uploaded yet.</div>
            ) : (
              <div className="divide-y divide-white/5 max-h-80 overflow-y-auto">
                {coupons.map(c => (
                  <div key={c.id} className="flex items-center justify-between px-6 py-3">
                    <p className="text-white font-mono text-sm font-black">{c.code}</p>
                    <div className="flex items-center gap-3">
                      {c.used && <p className="text-[9px] text-gray-500">{new Date(c.used_at).toLocaleDateString()}</p>}
                      <span className={`text-[9px] font-black uppercase px-2 py-1 rounded-full ${c.used ? "bg-red-500/20 text-red-400" : "bg-green-500/20 text-green-400"}`}>
                        {c.used ? "Used" : "Available"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}